import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';
import { Landmark, ChevronRight, RefreshCw, Download, Loader2, AlertTriangle, Filter, X, Eye, Search } from 'lucide-react';

import {
    fetchVendorOutStandingReport, fetchVendorOutStandingDetail,
    clearVendorOutStandingReport, clearVendorOutStandingDetail, resetVendorOutStandingReport,
    selectVOSReport, selectVOSReportLoading, selectVOSReportError,
    selectVOSDetail, selectVOSDetailLoading,
} from '../../slices/ReportsSlice/vendorOutStandingReportSlice';

const inputCls = 'w-full px-2.5 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg text-xs bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent';

const VENDOR_TYPES = [
    { id: 'SelectAll', label: 'Select All' },
    { id: 'Supplier', label: 'Supplier' },
    { id: 'ServiceProvider', label: 'Service Provider' },
];

const num = (v) => parseFloat(v || 0) || 0;
const money = (v) => num(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const DetailModal = ({ rows, loading, vendorLabel, onClose }) => {
    const totals = useMemo(() => rows.reduce((acc, r) => {
        acc.BasicBalance += num(r.BasicBalance);
        acc.RetentionBalance += num(r.RetentionBalance);
        acc.HoldBalance += num(r.HoldBalance);
        return acc;
    }, { BasicBalance: 0, RetentionBalance: 0, HoldBalance: 0 }), [rows]);

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[85vh] overflow-y-auto">
                <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-indigo-600 to-violet-600 rounded-t-2xl sticky top-0">
                    <h2 className="text-sm font-bold text-white">Invoice Detail — {vendorLabel}</h2>
                    <button onClick={onClose} className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"><X className="w-4 h-4 text-white" /></button>
                </div>
                <div className="p-4">
                    {loading ? (
                        <div className="flex items-center justify-center gap-3 py-10"><Loader2 className="h-5 w-5 animate-spin text-indigo-600" /><span className="text-sm text-gray-500">Loading…</span></div>
                    ) : rows.length === 0 ? (
                        <p className="text-center text-sm text-gray-400 py-8">No outstanding invoices found.</p>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="min-w-full text-[11px] divide-y divide-gray-200 dark:divide-gray-700">
                                <thead className="bg-gray-100 dark:bg-gray-900/40">
                                    <tr>
                                        {['CC Code', 'Invoice Date', 'Invoice No', 'PO No', 'Invoice Balance', 'Retention Balance', 'Hold Balance'].map((h) => (
                                            <th key={h} className="px-2 py-1.5 text-left font-bold text-gray-500 uppercase whitespace-nowrap">{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                                    {rows.map((r, i) => (
                                        <tr key={i} className="hover:bg-indigo-50 dark:hover:bg-indigo-900/10">
                                            <td className="px-2 py-1 whitespace-nowrap">{r.CCCode}</td>
                                            <td className="px-2 py-1 whitespace-nowrap">{r.InvoiceDate}</td>
                                            <td className="px-2 py-1 whitespace-nowrap">{r.Invoiceno}</td>
                                            <td className="px-2 py-1 whitespace-nowrap">{r.PoNo}</td>
                                            <td className="px-2 py-1 text-right">{money(r.BasicBalance)}</td>
                                            <td className="px-2 py-1 text-right">{money(r.RetentionBalance)}</td>
                                            <td className="px-2 py-1 text-right">{money(r.HoldBalance)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot>
                                    <tr className="bg-gray-100 dark:bg-gray-900/50 font-bold">
                                        <td className="px-2 py-1" colSpan={4}>TOTAL</td>
                                        <td className="px-2 py-1 text-right">{money(totals.BasicBalance)}</td>
                                        <td className="px-2 py-1 text-right">{money(totals.RetentionBalance)}</td>
                                        <td className="px-2 py-1 text-right">{money(totals.HoldBalance)}</td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const VendorOutStandingReport = () => {
    const dispatch = useDispatch();
    const { userData } = useSelector((s) => s.auth);
    const uid = userData?.UID || userData?.uid;
    const roleId = userData?.roleId || userData?.RID;

    const report = useSelector(selectVOSReport);
    const reportLoading = useSelector(selectVOSReportLoading);
    const reportError = useSelector(selectVOSReportError);
    const detail = useSelector(selectVOSDetail);
    const detailLoading = useSelector(selectVOSDetailLoading);

    const [vendorType, setVendorType] = useState('');
    const [ranParams, setRanParams] = useState(null);
    const [detailRow, setDetailRow] = useState(null);

    const handleReset = () => {
        setVendorType(''); setRanParams(null);
        dispatch(resetVendorOutStandingReport());
    };

    const runReport = () => {
        if (!vendorType) { toast.warning('Select Vendor Type'); return; }
        const params = { VendorType: vendorType, UID: uid, Roleid: roleId };
        setRanParams(params);
        dispatch(fetchVendorOutStandingReport(params));
    };

    const handleRefresh = () => { if (ranParams) dispatch(fetchVendorOutStandingReport(ranParams)); };

    const totals = useMemo(() => report.reduce((acc, r) => {
        acc.BasicBalance += num(r.BasicBalance);
        acc.RetentionBalance += num(r.RetentionBalance);
        acc.HoldBalance += num(r.HoldBalance);
        return acc;
    }, { BasicBalance: 0, RetentionBalance: 0, HoldBalance: 0 }), [report]);

    const openDetail = (row) => {
        setDetailRow(row);
        dispatch(fetchVendorOutStandingDetail({ Vendorcode: row.VendorCode }));
    };
    const closeDetail = () => { setDetailRow(null); dispatch(clearVendorOutStandingDetail()); };

    const handleExport = () => {
        if (report.length === 0) { toast.warning('No data to export'); return; }
        const exportRows = report.map((r) => ({
            'Vendor Code': r.VendorCode, 'Vendor Name': r.VendorName,
            'Invoice Balance': r.BasicBalance, 'Retention Balance': r.RetentionBalance, 'Hold Balance': r.HoldBalance,
        }));
        exportRows.push({ 'Vendor Code': '', 'Vendor Name': 'TOTAL', 'Invoice Balance': totals.BasicBalance, 'Retention Balance': totals.RetentionBalance, 'Hold Balance': totals.HoldBalance });
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(exportRows), 'Vendor Outstanding');
        XLSX.writeFile(wb, `Vendor_OutStanding_Report_${new Date().toISOString().slice(0, 10)}.xlsx`);
        toast.success('Excel downloaded');
    };

    return (
        <div className="space-y-3 p-4 text-xs">
            {/* Header */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-3 border border-gray-200 dark:border-gray-700">
                <div className="flex items-start justify-between flex-wrap gap-3">
                    <div>
                        <h1 className="text-base font-bold text-gray-900 dark:text-white flex items-center gap-2">
                            <div className="p-1.5 bg-gradient-to-br from-indigo-500 to-violet-600 rounded-lg">
                                <Landmark className="h-4 w-4 text-white" />
                            </div>
                            Vendor Current Outstanding Report
                        </h1>
                        <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">Vendor-wise current invoice, retention and hold balances</p>
                        <nav className="flex items-center gap-1.5 text-[11px] text-gray-400 mt-1">
                            <span>Reports</span><ChevronRight className="w-3 h-3" /><span className="text-gray-700 dark:text-gray-200">Vendor OutStanding Report</span>
                        </nav>
                    </div>
                    <div className="flex items-center gap-2">
                        <button onClick={handleRefresh} disabled={reportLoading || !ranParams} className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs font-medium transition-colors disabled:opacity-60">
                            <RefreshCw className={`h-3.5 w-3.5 ${reportLoading ? 'animate-spin' : ''}`} />Refresh
                        </button>
                        <button onClick={handleExport} disabled={report.length === 0} className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-lg text-xs font-medium transition-colors disabled:opacity-60">
                            <Download className="h-3.5 w-3.5" />Export Excel
                        </button>
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-3 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-1.5 mb-2">
                    <Filter className="h-3.5 w-3.5 text-indigo-600" />
                    <h2 className="text-xs font-semibold text-gray-700 dark:text-gray-300">Filters</h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
                    <div>
                        <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1">Vendor Type</label>
                        <select className={inputCls} value={vendorType} onChange={(e) => setVendorType(e.target.value)}>
                            <option value="">-Please Select-</option>
                            {VENDOR_TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
                        </select>
                    </div>
                    <div className="flex gap-2">
                        <button onClick={runReport} disabled={reportLoading} className="flex-1 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs font-semibold transition-colors disabled:opacity-60 flex items-center justify-center gap-1.5">
                            <Search className="w-3.5 h-3.5" /> View
                        </button>
                        <button onClick={handleReset} className="flex-1 px-3 py-1.5 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg text-xs font-semibold transition-colors">Reset</button>
                    </div>
                </div>
            </div>

            {/* Report */}
            {reportLoading ? (
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 flex items-center justify-center gap-3 py-10">
                    <Loader2 className="h-6 w-6 animate-spin text-indigo-600" /><span className="text-xs text-gray-500 dark:text-gray-400">Loading report…</span>
                </div>
            ) : reportError ? (
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 flex items-center justify-center gap-3 py-10 text-red-500">
                    <AlertTriangle className="h-5 w-5" /><span className="text-xs">{reportError}</span>
                </div>
            ) : ranParams && report.length === 0 ? (
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 py-10 text-center text-xs text-gray-400">No Data Found</div>
            ) : report.length > 0 ? (
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-700">
                        <h2 className="text-xs font-bold text-gray-700 dark:text-gray-300">Vendor Outstanding Summary</h2>
                    </div>
                    <div className="overflow-auto max-h-[70vh]">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                            <thead className="bg-gradient-to-r from-indigo-600 to-violet-600 sticky top-0 z-10">
                                <tr>
                                    <th className="px-2 py-1.5 text-center text-[10px] font-bold text-white uppercase">Detail</th>
                                    <th className="px-2 py-1.5 text-left text-[10px] font-bold text-white uppercase whitespace-nowrap">Vendor Code</th>
                                    <th className="px-2 py-1.5 text-left text-[10px] font-bold text-white uppercase whitespace-nowrap">Vendor Name</th>
                                    <th className="px-2 py-1.5 text-right text-[10px] font-bold text-white uppercase whitespace-nowrap">Invoice Balance</th>
                                    <th className="px-2 py-1.5 text-right text-[10px] font-bold text-white uppercase whitespace-nowrap">Retention Balance</th>
                                    <th className="px-2 py-1.5 text-right text-[10px] font-bold text-white uppercase whitespace-nowrap">Hold Balance</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                                {report.map((row, i) => (
                                    <tr key={i} className="hover:bg-indigo-50 dark:hover:bg-indigo-900/10 transition-colors">
                                        <td className="px-2 py-1 text-center">
                                            <button onClick={() => openDetail(row)} className="inline-flex text-indigo-600 hover:text-indigo-800">
                                                <Eye className="w-3.5 h-3.5" />
                                            </button>
                                        </td>
                                        <td className="px-2 py-1 text-xs whitespace-nowrap">{row.VendorCode}</td>
                                        <td className="px-2 py-1 text-xs text-gray-800 dark:text-gray-100 whitespace-nowrap">{row.VendorName}</td>
                                        <td className="px-2 py-1 text-xs text-right whitespace-nowrap">{money(row.BasicBalance)}</td>
                                        <td className="px-2 py-1 text-xs text-right whitespace-nowrap">{money(row.RetentionBalance)}</td>
                                        <td className="px-2 py-1 text-xs text-right whitespace-nowrap">{money(row.HoldBalance)}</td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr className="bg-gray-100 dark:bg-gray-900/50 font-bold">
                                    <td className="px-2 py-1 text-xs" colSpan={3}>TOTAL</td>
                                    <td className="px-2 py-1 text-xs text-right">{money(totals.BasicBalance)}</td>
                                    <td className="px-2 py-1 text-xs text-right">{money(totals.RetentionBalance)}</td>
                                    <td className="px-2 py-1 text-xs text-right">{money(totals.HoldBalance)}</td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </div>
            ) : null}

            {detailRow && (
                <DetailModal rows={detail} loading={detailLoading}
                    vendorLabel={`${detailRow.VendorName || detailRow.VendorCode}`}
                    onClose={closeDetail} />
            )}
        </div>
    );
};

export default VendorOutStandingReport;
